import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { VendorApplicationFormData } from '../types'

interface VendorApplicationFormProps {
  onClose: () => void
}

export default function VendorApplicationForm({ onClose }: VendorApplicationFormProps) {
  const { user, isLoggedIn } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<VendorApplicationFormData>({
    businessName: '',
    websiteUrl: '',
    businessEmail: '',
    businessPhone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    businessType: '',
    yearEstablished: '',
    businessDescription: '',
    productTypes: [],
    averageMonthlyVolume: '',
    targetMarkets: [],
    businessLicense: '',
    taxId: '',
    hasBusinessBanking: false,
    bankName: '',
    accountType: '',
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: '',
    redditUrl: '',
    otherSocialUrl: '',
    hasThirdPartyTesting: false,
    testingLab: '',
    testingFrequency: '',
    hasQualityCertifications: false,
    certifications: [],
    domesticShipping: false,
    internationalShipping: false,
    shippingRegions: [],
    averageShippingTime: '',
    shippingMethods: [],
    supportHours: '',
    supportMethods: [],
    returnPolicy: '',
    warrantyPolicy: '',
    additionalInfo: '',
    agreeToTerms: false,
    agreeToFees: false
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  if (!isLoggedIn) {
    return (
      <div className="auth-form-overlay">
        <div className="auth-form">
          <div className="auth-form-header">
            <h2>Login Required</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="auth-form-content">
            <p>You must be logged in to apply for vendor verification. Please log in or create an account first.</p>
            <button className="submit-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    )
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleArrayInputChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value ? value.split(',').map(item => item.trim()) : []
    }))
  }

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {}

    switch (step) {
      case 1: // Basic Business Information
        if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required'
        if (!formData.websiteUrl.trim()) newErrors.websiteUrl = 'Website URL is required'
        if (!formData.businessEmail.trim()) newErrors.businessEmail = 'Business email is required'
        if (!formData.businessPhone.trim()) newErrors.businessPhone = 'Business phone is required'
        if (!formData.businessType) newErrors.businessType = 'Business type is required'
        if (!formData.yearEstablished) newErrors.yearEstablished = 'Year established is required'
        break

      case 2: // Address & Description
        if (!formData.streetAddress.trim()) newErrors.streetAddress = 'Street address is required'
        if (!formData.city.trim()) newErrors.city = 'City is required'
        if (!formData.state.trim()) newErrors.state = 'State is required'
        if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required'
        if (!formData.businessDescription.trim()) newErrors.businessDescription = 'Business description is required'
        break

      case 3: // Products & Operations
        if (formData.productTypes.length === 0) newErrors.productTypes = 'At least one product type is required'
        if (!formData.averageMonthlyVolume) newErrors.averageMonthlyVolume = 'Monthly volume is required'
        break

      case 4: // Quality & Shipping
        if (!formData.averageShippingTime) newErrors.averageShippingTime = 'Average shipping time is required'
        if (formData.shippingMethods.length === 0) newErrors.shippingMethods = 'At least one shipping method is required'
        break

      case 5: // Customer Service & Terms
        if (!formData.supportHours.trim()) newErrors.supportHours = 'Support hours are required'
        if (formData.supportMethods.length === 0) newErrors.supportMethods = 'At least one support method is required'
        if (!formData.returnPolicy.trim()) newErrors.returnPolicy = 'Return policy is required'
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms'
        if (!formData.agreeToFees) newErrors.agreeToFees = 'You must agree to the verification fees'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => prev - 1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateStep(5)) return

    setIsSubmitting(true)

    try {
      // Simulate API call - in real app, this would submit to backend
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Save application to localStorage (in real app, this would be API call)
      const applications = JSON.parse(localStorage.getItem('vendor-applications') || '[]')
      const newApplication = {
        id: Date.now().toString(),
        userId: user!.id,
        businessName: formData.businessName,
        websiteUrl: formData.websiteUrl,
        businessEmail: formData.businessEmail,
        businessPhone: formData.businessPhone,
        businessAddress: {
          street: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        businessType: formData.businessType as any,
        yearEstablished: parseInt(formData.yearEstablished),
        businessDescription: formData.businessDescription,
        productTypes: formData.productTypes,
        averageMonthlyVolume: formData.averageMonthlyVolume,
        targetMarkets: formData.targetMarkets,
        businessLicense: formData.businessLicense || undefined,
        taxId: formData.taxId || undefined,
        bankingInformation: {
          hasBusinessBanking: formData.hasBusinessBanking,
          bankName: formData.bankName || undefined,
          accountType: formData.accountType || undefined
        },
        socialMedia: {
          facebook: formData.facebookUrl || undefined,
          instagram: formData.instagramUrl || undefined,
          twitter: formData.twitterUrl || undefined,
          reddit: formData.redditUrl || undefined,
          other: formData.otherSocialUrl || undefined
        },
        qualityAssurance: {
          hasThirdPartyTesting: formData.hasThirdPartyTesting,
          testingLab: formData.testingLab || undefined,
          testingFrequency: formData.testingFrequency || undefined,
          hasQualityCertifications: formData.hasQualityCertifications,
          certifications: formData.certifications.length > 0 ? formData.certifications : undefined
        },
        shippingInfo: {
          domesticShipping: formData.domesticShipping,
          internationalShipping: formData.internationalShipping,
          shippingRegions: formData.shippingRegions,
          averageShippingTime: formData.averageShippingTime,
          shippingMethods: formData.shippingMethods
        },
        customerService: {
          supportHours: formData.supportHours,
          supportMethods: formData.supportMethods,
          returnPolicy: formData.returnPolicy,
          warrantyPolicy: formData.warrantyPolicy || undefined
        },
        additionalInfo: formData.additionalInfo || undefined,
        applicationDate: new Date(),
        status: 'pending' as const,
        lastUpdated: new Date()
      }

      applications.push(newApplication)
      localStorage.setItem('vendor-applications', JSON.stringify(applications))

      alert('Application submitted successfully! You will receive an email confirmation shortly.')
      onClose()
    } catch (error) {
      console.error('Submission error:', error)
      setErrors({ submit: 'Failed to submit application. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <h3>Basic Business Information</h3>
            <div className="form-group">
              <label htmlFor="businessName">Business Name *</label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className={errors.businessName ? 'error' : ''}
                placeholder="Your Company Name"
              />
              {errors.businessName && <span className="error-message">{errors.businessName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="websiteUrl">Website URL *</label>
              <input
                type="url"
                id="websiteUrl"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleInputChange}
                className={errors.websiteUrl ? 'error' : ''}
                placeholder="https://yourwebsite.com"
              />
              {errors.websiteUrl && <span className="error-message">{errors.websiteUrl}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="businessEmail">Business Email *</label>
                <input
                  type="email"
                  id="businessEmail"
                  name="businessEmail"
                  value={formData.businessEmail}
                  onChange={handleInputChange}
                  className={errors.businessEmail ? 'error' : ''}
                  placeholder="contact@yourcompany.com"
                />
                {errors.businessEmail && <span className="error-message">{errors.businessEmail}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="businessPhone">Business Phone *</label>
                <input
                  type="tel"
                  id="businessPhone"
                  name="businessPhone"
                  value={formData.businessPhone}
                  onChange={handleInputChange}
                  className={errors.businessPhone ? 'error' : ''}
                  placeholder="(555) 123-4567"
                />
                {errors.businessPhone && <span className="error-message">{errors.businessPhone}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="businessType">Business Type *</label>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  className={errors.businessType ? 'error' : ''}
                >
                  <option value="">Select Business Type</option>
                  <option value="LLC">LLC</option>
                  <option value="Corporation">Corporation</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Sole Proprietorship">Sole Proprietorship</option>
                  <option value="Other">Other</option>
                </select>
                {errors.businessType && <span className="error-message">{errors.businessType}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="yearEstablished">Year Established *</label>
                <input
                  type="number"
                  id="yearEstablished"
                  name="yearEstablished"
                  value={formData.yearEstablished}
                  onChange={handleInputChange}
                  className={errors.yearEstablished ? 'error' : ''}
                  min="1900"
                  max={new Date().getFullYear()}
                  placeholder="2020"
                />
                {errors.yearEstablished && <span className="error-message">{errors.yearEstablished}</span>}
              </div>
            </div>
          </>
        )

      case 2:
        return (
          <>
            <h3>Business Address & Description</h3>
            <div className="form-group">
              <label htmlFor="streetAddress">Street Address *</label>
              <input
                type="text"
                id="streetAddress"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleInputChange}
                className={errors.streetAddress ? 'error' : ''}
                placeholder="123 Business Street"
              />
              {errors.streetAddress && <span className="error-message">{errors.streetAddress}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={errors.city ? 'error' : ''}
                  placeholder="Your City"
                />
                {errors.city && <span className="error-message">{errors.city}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="state">State *</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={errors.state ? 'error' : ''}
                  placeholder="CA"
                />
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="zipCode">ZIP Code *</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  className={errors.zipCode ? 'error' : ''}
                  placeholder="12345"
                />
                {errors.zipCode && <span className="error-message">{errors.zipCode}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="businessDescription">Business Description *</label>
              <textarea
                id="businessDescription"
                name="businessDescription"
                value={formData.businessDescription}
                onChange={handleInputChange}
                className={errors.businessDescription ? 'error' : ''}
                rows={4}
                placeholder="Describe your business, mission, and what makes you unique..."
              />
              {errors.businessDescription && <span className="error-message">{errors.businessDescription}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="businessLicense">Business License # (Optional)</label>
                <input
                  type="text"
                  id="businessLicense"
                  name="businessLicense"
                  value={formData.businessLicense}
                  onChange={handleInputChange}
                  placeholder="License number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="taxId">Tax ID / EIN (Optional)</label>
                <input
                  type="text"
                  id="taxId"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleInputChange}
                  placeholder="XX-XXXXXXX"
                />
              </div>
            </div>
          </>
        )

      case 3:
        return (
          <>
            <h3>Products & Operations</h3>
            <div className="form-group">
              <label htmlFor="productTypes">Product Types * (comma-separated)</label>
              <input
                type="text"
                id="productTypes"
                name="productTypes"
                value={formData.productTypes.join(', ')}
                onChange={(e) => handleArrayInputChange('productTypes', e.target.value)}
                className={errors.productTypes ? 'error' : ''}
                placeholder="Kratom Powder, Capsules, Extracts, 7-OH Products"
              />
              {errors.productTypes && <span className="error-message">{errors.productTypes}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="averageMonthlyVolume">Average Monthly Sales Volume *</label>
              <select
                id="averageMonthlyVolume"
                name="averageMonthlyVolume"
                value={formData.averageMonthlyVolume}
                onChange={handleInputChange}
                className={errors.averageMonthlyVolume ? 'error' : ''}
              >
                <option value="">Select Volume Range</option>
                <option value="Under $10,000">Under $10,000</option>
                <option value="$10,000 - $50,000">$10,000 - $50,000</option>
                <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                <option value="$100,000 - $500,000">$100,000 - $500,000</option>
                <option value="Over $500,000">Over $500,000</option>
              </select>
              {errors.averageMonthlyVolume && <span className="error-message">{errors.averageMonthlyVolume}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="targetMarkets">Target Markets (comma-separated)</label>
              <input
                type="text"
                id="targetMarkets"
                name="targetMarkets"
                value={formData.targetMarkets.join(', ')}
                onChange={(e) => handleArrayInputChange('targetMarkets', e.target.value)}
                placeholder="Retail Customers, Wholesale, B2B"
              />
            </div>

            <div className="checkbox-section">
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="hasBusinessBanking"
                    checked={formData.hasBusinessBanking}
                    onChange={handleInputChange}
                  />
                  <span>We have dedicated business banking</span>
                </label>
              </div>

              {formData.hasBusinessBanking && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bankName">Bank Name</label>
                    <input
                      type="text"
                      id="bankName"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      placeholder="Bank of America"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="accountType">Account Type</label>
                    <select
                      id="accountType"
                      name="accountType"
                      value={formData.accountType}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Account Type</option>
                      <option value="Business Checking">Business Checking</option>
                      <option value="Business Savings">Business Savings</option>
                      <option value="Merchant Account">Merchant Account</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </>
        )

      case 4:
        return (
          <>
            <h3>Quality Assurance & Shipping</h3>
            
            <div className="quality-section">
              <h4>Quality Assurance</h4>
              <div className="checkbox-section">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hasThirdPartyTesting"
                      checked={formData.hasThirdPartyTesting}
                      onChange={handleInputChange}
                    />
                    <span>We use third-party testing</span>
                  </label>
                </div>

                {formData.hasThirdPartyTesting && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="testingLab">Testing Lab</label>
                      <input
                        type="text"
                        id="testingLab"
                        name="testingLab"
                        value={formData.testingLab}
                        onChange={handleInputChange}
                        placeholder="Lab name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="testingFrequency">Testing Frequency</label>
                      <select
                        id="testingFrequency"
                        name="testingFrequency"
                        value={formData.testingFrequency}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Frequency</option>
                        <option value="Every Batch">Every Batch</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                        <option value="Bi-Annually">Bi-Annually</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="hasQualityCertifications"
                      checked={formData.hasQualityCertifications}
                      onChange={handleInputChange}
                    />
                    <span>We have quality certifications</span>
                  </label>
                </div>

                {formData.hasQualityCertifications && (
                  <div className="form-group">
                    <label htmlFor="certifications">Certifications (comma-separated)</label>
                    <input
                      type="text"
                      id="certifications"
                      name="certifications"
                      value={formData.certifications.join(', ')}
                      onChange={(e) => handleArrayInputChange('certifications', e.target.value)}
                      placeholder="GMP, ISO, FDA Registered"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="shipping-section">
              <h4>Shipping Information</h4>
              <div className="checkbox-section">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="domesticShipping"
                      checked={formData.domesticShipping}
                      onChange={handleInputChange}
                    />
                    <span>Domestic shipping (US)</span>
                  </label>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="internationalShipping"
                      checked={formData.internationalShipping}
                      onChange={handleInputChange}
                    />
                    <span>International shipping</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="shippingRegions">Shipping Regions (comma-separated)</label>
                <input
                  type="text"
                  id="shippingRegions"
                  name="shippingRegions"
                  value={formData.shippingRegions.join(', ')}
                  onChange={(e) => handleArrayInputChange('shippingRegions', e.target.value)}
                  placeholder="United States, Canada, Europe"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="averageShippingTime">Average Shipping Time *</label>
                  <select
                    id="averageShippingTime"
                    name="averageShippingTime"
                    value={formData.averageShippingTime}
                    onChange={handleInputChange}
                    className={errors.averageShippingTime ? 'error' : ''}
                  >
                    <option value="">Select Shipping Time</option>
                    <option value="Same Day">Same Day</option>
                    <option value="1-2 Business Days">1-2 Business Days</option>
                    <option value="3-5 Business Days">3-5 Business Days</option>
                    <option value="5-7 Business Days">5-7 Business Days</option>
                    <option value="1-2 Weeks">1-2 Weeks</option>
                  </select>
                  {errors.averageShippingTime && <span className="error-message">{errors.averageShippingTime}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="shippingMethods">Shipping Methods * (comma-separated)</label>
                <input
                  type="text"
                  id="shippingMethods"
                  name="shippingMethods"
                  value={formData.shippingMethods.join(', ')}
                  onChange={(e) => handleArrayInputChange('shippingMethods', e.target.value)}
                  className={errors.shippingMethods ? 'error' : ''}
                  placeholder="USPS, UPS, FedEx, DHL"
                />
                {errors.shippingMethods && <span className="error-message">{errors.shippingMethods}</span>}
              </div>
            </div>
          </>
        )

      case 5:
        return (
          <>
            <h3>Customer Service & Social Media</h3>
            
            <div className="customer-service-section">
              <h4>Customer Service</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="supportHours">Support Hours *</label>
                  <input
                    type="text"
                    id="supportHours"
                    name="supportHours"
                    value={formData.supportHours}
                    onChange={handleInputChange}
                    className={errors.supportHours ? 'error' : ''}
                    placeholder="Mon-Fri 9AM-5PM EST"
                  />
                  {errors.supportHours && <span className="error-message">{errors.supportHours}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="supportMethods">Support Methods * (comma-separated)</label>
                <input
                  type="text"
                  id="supportMethods"
                  name="supportMethods"
                  value={formData.supportMethods.join(', ')}
                  onChange={(e) => handleArrayInputChange('supportMethods', e.target.value)}
                  className={errors.supportMethods ? 'error' : ''}
                  placeholder="Email, Phone, Live Chat, Social Media"
                />
                {errors.supportMethods && <span className="error-message">{errors.supportMethods}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="returnPolicy">Return Policy *</label>
                <textarea
                  id="returnPolicy"
                  name="returnPolicy"
                  value={formData.returnPolicy}
                  onChange={handleInputChange}
                  className={errors.returnPolicy ? 'error' : ''}
                  rows={3}
                  placeholder="Describe your return/refund policy..."
                />
                {errors.returnPolicy && <span className="error-message">{errors.returnPolicy}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="warrantyPolicy">Warranty Policy (Optional)</label>
                <textarea
                  id="warrantyPolicy"
                  name="warrantyPolicy"
                  value={formData.warrantyPolicy}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Describe any warranties you offer..."
                />
              </div>
            </div>

            <div className="social-media-section">
              <h4>Social Media (Optional)</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="facebookUrl">Facebook</label>
                  <input
                    type="url"
                    id="facebookUrl"
                    name="facebookUrl"
                    value={formData.facebookUrl}
                    onChange={handleInputChange}
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="instagramUrl">Instagram</label>
                  <input
                    type="url"
                    id="instagramUrl"
                    name="instagramUrl"
                    value={formData.instagramUrl}
                    onChange={handleInputChange}
                    placeholder="https://instagram.com/yourpage"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="twitterUrl">Twitter</label>
                  <input
                    type="url"
                    id="twitterUrl"
                    name="twitterUrl"
                    value={formData.twitterUrl}
                    onChange={handleInputChange}
                    placeholder="https://twitter.com/yourpage"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="redditUrl">Reddit</label>
                  <input
                    type="url"
                    id="redditUrl"
                    name="redditUrl"
                    value={formData.redditUrl}
                    onChange={handleInputChange}
                    placeholder="https://reddit.com/r/yoursubreddit"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="additionalInfo">Additional Information (Optional)</label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                rows={4}
                placeholder="Any additional information you'd like to share about your business..."
              />
            </div>

            <div className="terms-section">
              <h4>Terms & Agreements</h4>
              <div className="checkbox-section">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                    />
                    <span>I agree to the Terms of Service and understand that providing false information may result in application rejection</span>
                  </label>
                  {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="agreeToFees"
                      checked={formData.agreeToFees}
                      onChange={handleInputChange}
                    />
                    <span>I understand and agree to pay the verification fee ($199/year) upon approval</span>
                  </label>
                  {errors.agreeToFees && <span className="error-message">{errors.agreeToFees}</span>}
                </div>
              </div>
            </div>
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className="auth-form-overlay">
      <div className="vendor-application-form">
        <div className="auth-form-header">
          <h2>Vendor Verification Application</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="application-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / 5) * 100}%` }}
            ></div>
          </div>
          <div className="step-indicators">
            {[1, 2, 3, 4, 5].map(step => (
              <div 
                key={step} 
                className={`step-indicator ${step === currentStep ? 'active' : ''} ${step < currentStep ? 'completed' : ''}`}
              >
                {step}
              </div>
            ))}
          </div>
          <div className="step-labels">
            <span>Basic Info</span>
            <span>Address</span>
            <span>Products</span>
            <span>Quality</span>
            <span>Service</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-form-content">
          {renderStep()}

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <div className="form-navigation">
            {currentStep > 1 && (
              <button
                type="button"
                className="nav-btn prev-btn"
                onClick={prevStep}
              >
                Previous
              </button>
            )}

            {currentStep < 5 ? (
              <button
                type="button"
                className="nav-btn next-btn"
                onClick={nextStep}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}